import { Alert, Box } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';

import { useAnalytics } from '~/analytics';
import { useAppBarHeight } from '~/components/global/appBarHeight';
import type { CfiFinding, CfiRating, CfiReport } from '~/data/hooks/useCfi';
import { useCfiMutations } from '~/data/hooks/useCfi';

import { CfiAddCustomFindingDialog } from './CfiAddCustomFindingDialog';
import { CfiBetaNotice } from './CfiBetaNotice';
import { CfiFindingRow } from './CfiFindingRow';
import { CfiPublishBar } from './CfiPublishBar';
import { CfiReportNarrative } from './CfiReportNarrative';
import { CfiSummaryDashboard } from './CfiSummaryDashboard';
import { finalCounts } from './finalCounts';

type Props = {
  contractId: string;
  report: CfiReport;
  canEdit: boolean;
  /** See `cfiLayout` — laid out for the full page rather than the drawer. */
  wide?: boolean;
};

export const CfiReportEditor = ({ contractId, report, canEdit, wide = false }: Props) => {
  const { patchFinding, patchReport, publish, createCustomFinding } = useCfiMutations(contractId);
  const { track } = useAnalytics();
  const [isAddOpen, setIsAddOpen] = useState(false);

  const counts = useMemo(() => finalCounts(report.findings), [report.findings]);

  // A zero-height marker between the summary card and the action bar. It crosses under
  // the AppBar at exactly the moment the bar pins there, so one observation drives both
  // the bar's chrome and its tally.
  //
  // `rootMargin` has to match the bar's own `top`, or the marker still counts as visible
  // while it sits behind the fixed header and the bar pins a header's height early.
  const appBarHeight = useAppBarHeight();
  const summaryEndRef = useRef<HTMLDivElement | null>(null);
  const [isBarPinned, setIsBarPinned] = useState(false);
  useEffect(() => {
    const marker = summaryEndRef.current;
    // jsdom has no IntersectionObserver; the bar simply renders unpinned there,
    // which is the state every test asserts against.
    if (!marker || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(([entry]) => setIsBarPinned(!entry.isIntersecting), {
      rootMargin: `-${appBarHeight}px 0px 0px 0px`,
    });
    observer.observe(marker);
    return () => observer.disconnect();
  }, [appBarHeight]);

  const handlePatch = async (
    finding: CfiFinding,
    body: Partial<{
      consultantRating: CfiRating | null;
      consultantSummary: string;
      consultantRecommendedAction: string;
      agreedWithAgent: boolean | null;
      excludedFromReport: boolean;
    }>,
  ) => {
    await patchFinding(finding, body);
    // Track after the PATCH resolves so failed saves don't inflate counts.
    if (body.excludedFromReport === true) {
      track('cfi_finding_dropped', { report_id: report.id, finding_source: finding.source });
      return;
    }
    if (body.excludedFromReport === false) return; // undrop — intentionally untracked
    const fields: Array<'rating' | 'summary' | 'recommended_action'> = [];
    if ('consultantRating' in body) fields.push('rating');
    if ('consultantSummary' in body) fields.push('summary');
    if ('consultantRecommendedAction' in body) fields.push('recommended_action');
    if (fields.length === 0) return;
    track('cfi_finding_edited', {
      report_id: report.id,
      finding_source: finding.source,
      fields,
      // The model-trust signal: did the consultant change the agent's rating?
      // Always false for consultant-custom findings (agentRating is null).
      overrode_agent_rating:
        body.consultantRating != null &&
        finding.agentRating != null &&
        body.consultantRating !== finding.agentRating,
    });
  };

  const handlePublish = async () => {
    try {
      await publish(report.id);
      track('cfi_report_published', {
        report_id: report.id,
        finding_count: Object.values(counts).reduce((sum, n) => sum + n, 0),
        unfavorable_count: counts.unfavorable,
        republish: report.previousPublishedAt != null,
      });
    } catch (err) {
      console.error('CFI publish failed', err);
    }
  };

  const handleCreateCustom = async (body: {
    provisionLabel: string;
    rating: CfiRating;
    rationale: string;
  }) => {
    await createCustomFinding(report.id, body);
    track('cfi_custom_finding_added', { report_id: report.id, rating: body.rating });
  };

  return (
    <Box sx={{ width: '100%' }}>
      <CfiBetaNotice />

      {!canEdit ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          This report is in review by a consultant. You can view findings but not edit them.
        </Alert>
      ) : null}

      <CfiSummaryDashboard counts={counts} flush={canEdit} />
      <Box ref={summaryEndRef} aria-hidden />
      <CfiPublishBar
        canEdit={canEdit}
        counts={counts}
        reportId={report.id}
        onPublish={handlePublish}
        onAddFinding={() => setIsAddOpen(true)}
        condensed={isBarPinned}
      />

      <CfiReportNarrative
        title="Overall assessment"
        value={report.finalScopeSummary}
        canEdit={canEdit}
        placeholder="Give the owner a verdict on this agreement…"
        onSave={async (next) => {
          await patchReport(report.id, { consultantScopeSummary: next });
        }}
      />

      {report.findings
        .filter((f) => !f.excludedFromReport || canEdit)
        .map((finding) => (
          <CfiFindingRow
            key={finding.id}
            finding={finding}
            canEdit={canEdit}
            onPatch={handlePatch}
            wide={wide}
          />
        ))}

      {/* "Add custom finding" and Publish live in the action bar above, which is on
          screen at every scroll position. */}

      <CfiAddCustomFindingDialog
        open={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        onSubmit={handleCreateCustom}
      />
    </Box>
  );
};
